import type { PlatformAppId } from "../config/environment";

export type MockApiScenario = "success" | "empty" | "error" | "unauthorized" | "slow";

export interface MockApiEnvelope<T> {
  data: T;
  meta: {
    app: PlatformAppId;
    requestId: string;
    timestamp: string;
    scenario: MockApiScenario;
  };
}

export interface MockApiRequest<T> {
  app: PlatformAppId;
  data: T;
  emptyData: T;
  scenario?: MockApiScenario;
  latencyMs?: number;
}

export class MockApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
    this.name = "MockApiError";
  }
}

const wait = (duration: number) => new Promise(resolve => globalThis.setTimeout(resolve, duration));

export function currentMockScenario(
  search = typeof window === "undefined" ? "" : window.location.search,
): MockApiScenario {
  const requested = new URLSearchParams(search).get("mock");
  return ["empty", "error", "unauthorized", "slow"].includes(requested || "")
    ? (requested as MockApiScenario)
    : "success";
}

export async function mockApiRequest<T>({
  app,
  data,
  emptyData,
  scenario = currentMockScenario(),
  latencyMs = 80,
}: MockApiRequest<T>): Promise<MockApiEnvelope<T>> {
  await wait(scenario === "slow" ? Math.max(latencyMs, 900) : latencyMs);

  if (scenario === "unauthorized") {
    throw new MockApiError("The mock session is not authorized for this workspace.", 401, "UNAUTHORIZED");
  }
  if (scenario === "error") {
    throw new MockApiError(
      "The mock Academy service is temporarily unavailable.",
      503,
      "SERVICE_UNAVAILABLE",
    );
  }

  return {
    data: scenario === "empty" ? emptyData : data,
    meta: {
      app,
      requestId: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      scenario,
    },
  };
}

export function bootstrapMockApi(app: PlatformAppId) {
  return mockApiRequest({
    app,
    data: { ready: true },
    emptyData: { ready: true },
  });
}
